/**
 * SubscriptionGuard
 * 
 * Redireciona para /billing se tenant não tiver assinatura ativa
 * 
 * IMPORTANTE: Este é apenas UX (melhora experiência).
 * A SEGURANÇA REAL está no backend (RLS + guards em edge functions).
 */

import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useSession } from "@/state/session";

interface SubscriptionGuardProps {
  children: React.ReactNode;
}

export function SubscriptionGuard({ children }: SubscriptionGuardProps) {
  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useSession();

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setIsChecking(false);
      return;
    }

    checkSubscriptionStatus();
  }, [user, isAuthenticated]);

  const checkSubscriptionStatus = async () => {
    try {
      // Buscar tenant do usuário
      const { data: tenantUser, error: tenantUserError } = await supabase
        .from("tenant_users")
        .select("tenant_id")
        .eq("user_id", user!.id)
        .single();

      if (tenantUserError || !tenantUser) {
        console.error("❌ Erro ao buscar tenant:", tenantUserError);
        setHasAccess(false);
        setIsChecking(false);
        
        // Auditar bloqueio
        await supabase.rpc("log_billing_audit", {
          p_tenant_id: null,
          p_user_id: user!.id,
          p_event_type: "access_denied",
          p_subscription_status: "no_tenant",
          p_route: location.pathname,
          p_details: { error: "Tenant não encontrado" }
        });
        
        navigate("/billing");
        return;
      }

      // Buscar status da assinatura, subscription_updated_at e trial_end do tenant
      const { data: tenant, error: tenantError } = await supabase
        .from("tenant")
        .select("subscription_status, subscription_updated_at, trial_end")
        .eq("id", tenantUser.tenant_id)
        .single();

      if (tenantError || !tenant) {
        console.error("❌ Erro ao buscar status:", tenantError);
        setHasAccess(false);
        setIsChecking(false);
        
        // Auditar bloqueio
        await supabase.rpc("log_billing_audit", {
          p_tenant_id: tenantUser.tenant_id,
          p_user_id: user!.id,
          p_event_type: "access_denied",
          p_subscription_status: "error",
          p_route: location.pathname,
          p_details: { error: tenantError?.message }
        });
        
        navigate("/billing");
        return;
      }

      const status = tenant.subscription_status;
      
      // Verificar grace period para past_due (7 dias)
      let isInGracePeriod = false;
      if (status === "past_due" && tenant.subscription_updated_at) {
        const updatedAt = new Date(tenant.subscription_updated_at);
        const now = new Date();
        const daysSinceUpdate = (now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60 * 24);
        isInGracePeriod = daysSinceUpdate <= 7;
      }

      // Feedback UX: avisar se trial está próximo de expirar
      if (status === "trialing" && tenant.trial_end) {
        const trialEnd = new Date(tenant.trial_end);
        const now = new Date();
        const daysRemaining = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysRemaining <= 7 && daysRemaining > 0) {
          console.log(`⏰ Trial expira em ${daysRemaining} dias - considere escolher um plano em /billing`);
        }
      }

      // Status válidos: active, trialing, ou past_due dentro do grace period
      const validStatuses = ["active", "trialing"];
      const hasValidAccess = validStatuses.includes(status) || (status === "past_due" && isInGracePeriod);

      if (!hasValidAccess) {
        console.log("⚠️ Assinatura inválida:", status);
        
        // Auditar bloqueio
        await supabase.rpc("log_billing_audit", {
          p_tenant_id: tenantUser.tenant_id,
          p_user_id: user!.id,
          p_event_type: "access_denied",
          p_subscription_status: status,
          p_route: location.pathname,
          p_details: { 
            reason: status === "past_due" ? "Grace period expirado" : "Status inválido",
            grace_period_active: isInGracePeriod 
          }
        });
        
        navigate("/billing");
        setHasAccess(false);
      } else {
        // Auditar acesso permitido
        await supabase.rpc("log_billing_audit", {
          p_tenant_id: tenantUser.tenant_id,
          p_user_id: user!.id,
          p_event_type: "login_success",
          p_subscription_status: status,
          p_route: location.pathname,
          p_details: { 
            grace_period_active: isInGracePeriod 
          }
        });
        
        setHasAccess(true);
      }
    } catch (error) {
      console.error("❌ Erro ao verificar assinatura:", error);
      
      // Auditar erro
      await supabase.rpc("log_billing_audit", {
        p_tenant_id: null,
        p_user_id: user!.id,
        p_event_type: "access_denied",
        p_subscription_status: "error",
        p_route: location.pathname,
        p_details: { error: String(error) }
      });
      
      navigate("/billing");
      setHasAccess(false);
    } finally {
      setIsChecking(false);
    }
  };

  if (isChecking) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" text="Verificando assinatura..." />
      </div>
    );
  }

  if (!hasAccess) {
    return null; // Já redirecionou para /billing
  }

  return <>{children}</>;
}
